
// * Import react hooks
import { useState, useCallback } from "react";

// * Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// * Import API
import { applicationTestAPI } from "../applicationsTests/applicationTestAPI";

// * Import types
import { type Pagination } from "../../../services/stores/types";

// * Import types
import {
  type ApplicationTestDetails,
  type CreateApplicationTestInput,
} from "../applicationsTests/types/types";

/**
 * ? Custom hook `useApplicationTests` to manage operations related to ApplicationTests
 * within the context of a specific application.
 *
 * @param {string} applicationId - ID of the application for context-specific operations.
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns applicationTests data, loading state, error messages, and functions to interact with the API.
 */
export const useApplicationTests = (applicationId: string, token: string) => {
  const queryClient = useQueryClient();

  // * States for search and pagination
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  // * Define the query key
  const queryKey = [
    "applicationTests",
    applicationId,
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  // ? Fetch applicationTests using react-query
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      applicationTestAPI.getAll(
        applicationId,
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000, // * Cache for 5 minutes
    enabled: !!applicationId && !!token, // * Only enable the query if applicationId and token exist
  });

  // * Extract data and pagination
  const applicationTests = data?.applicationTests || [];
  const fetchedPagination = data?.pagination || pagination;

  // ! Handlers for search and pagination
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // * Mutations to create, update, and delete applicationTests
  const createApplicationTestMutation = useMutation({
    mutationFn: (applicationTestData: CreateApplicationTestInput) =>
      applicationTestAPI.create(applicationId, applicationTestData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateApplicationTestMutation = useMutation({
    mutationFn: ({ id, test_id }: { id: string; test_id: string }) =>
      applicationTestAPI.update(applicationId, id, { test_id }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteApplicationTestMutation = useMutation({
    mutationFn: (id: string) =>
      applicationTestAPI.delete(applicationId, id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // TODO: Fetch details of an applicationTest by ID
  const getApplicationTestById = useCallback(
    async (id: string): Promise<ApplicationTestDetails | null> => {
      try {
        const applicationTestDetails = await applicationTestAPI.getById(
          applicationId,
          id,
          token
        );
        return applicationTestDetails;
      } catch (err) {
        console.error("Error fetching application test by ID:", err);
        return null;
      }
    },
    [applicationId, token]
  );

  // * Return values and functions
  return {
    applicationTests,
    loading: isLoading || isFetching,
    error: isError ? (error as Error).message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createApplicationTest: createApplicationTestMutation.mutate,
    updateApplicationTest: updateApplicationTestMutation.mutate,
    deleteApplicationTest: deleteApplicationTestMutation.mutate,
    getApplicationTestById,
    changePage,
    isCreating: createApplicationTestMutation.isPending,
    isUpdating: updateApplicationTestMutation.isPending,
    isDeleting: deleteApplicationTestMutation.isPending,
  };
};
